import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth-store';
import { usePricingStore } from '@/lib/pricing-store';
import { supabase } from '@/lib/supabase';
import { ResellersTab } from './resellers-tab';
import { UsersTab } from './users-tab';
import { QuotationsTab } from './quotations-tab';
import { SmtpTab } from './smtp-tab';
import { ApiKeysTab } from './api-keys-tab';
import { Button } from '@/components/ui/button';
import { LogOut, Users, Building2, ShieldCheck, LayoutGrid, Eye, EyeOff, FileText, Mail, KeyRound } from 'lucide-react';

type Tab = 'resellers' | 'users' | 'quotations' | 'smtp' | 'api-keys';

interface ResellerOption {
  id: string;
  name: string;
  discount_percent: number;
  margin_percent: number;
  brand_color: string | null;
}

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState<Tab>('resellers');
  const [resellers, setResellers] = useState<ResellerOption[]>([]);
  const { user, signOut } = useAuthStore();
  const { simulatedReseller, setSimulatedReseller } = usePricingStore();
  const navigate = useNavigate();

  useEffect(() => {
    supabase
      .from('resellers')
      .select('id, name, discount_percent, margin_percent, brand_color')
      .order('name')
      .then(({ data }) => { if (data) setResellers(data); });
  }, []);

  const handleSignOut = async () => {
    setSimulatedReseller(null);
    await signOut();
    navigate('/login');
  };

  const handleSimulateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    if (!id) {
      setSimulatedReseller(null);
      return;
    }
    const found = resellers.find(r => r.id === id);
    if (found) setSimulatedReseller(found);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#212b4f] text-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-slate-300" />
            <span className="font-semibold tracking-tight">Admin Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-300 text-sm">{user?.email}</span>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-white hover:bg-white/10 h-8 gap-1.5"
            >
              <LayoutGrid className="h-4 w-4" />
              Configurator
            </Button>
            <Button
              onClick={handleSignOut}
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-white hover:bg-white/10 h-8 gap-1.5"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm">
            <button
              onClick={() => setActiveTab('resellers')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'resellers'
                  ? 'bg-[#212b4f] text-white'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Building2 className="h-4 w-4" />
              Resellers
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'users'
                  ? 'bg-[#212b4f] text-white'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Users className="h-4 w-4" />
              Users
            </button>
            <button
              onClick={() => setActiveTab('quotations')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'quotations'
                  ? 'bg-[#212b4f] text-white'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <FileText className="h-4 w-4" />
              Quotations
            </button>
            <button
              onClick={() => setActiveTab('smtp')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'smtp'
                  ? 'bg-[#212b4f] text-white'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Mail className="h-4 w-4" />
              SMTP
            </button>
            <button
              onClick={() => setActiveTab('api-keys')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'api-keys'
                  ? 'bg-[#212b4f] text-white'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <KeyRound className="h-4 w-4" />
              API Keys
            </button>
          </div>

          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm ml-auto">
            {simulatedReseller ? (
              <Eye className="h-4 w-4 text-amber-500 shrink-0" />
            ) : (
              <EyeOff className="h-4 w-4 text-slate-400 shrink-0" />
            )}
            <span className="text-xs font-medium text-slate-500 whitespace-nowrap">Simulate reseller:</span>
            <select
              value={simulatedReseller?.id ?? ''}
              onChange={handleSimulateChange}
              className="text-sm border-0 bg-transparent focus:outline-none text-slate-700 pr-1 min-w-[120px]"
            >
              <option value="">— Base prices —</option>
              {resellers.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name} ({r.discount_percent}% / {r.margin_percent}%)
                </option>
              ))}
            </select>
            {simulatedReseller && (
              <button
                onClick={() => setSimulatedReseller(null)}
                className="text-xs text-amber-600 hover:text-amber-800 font-medium whitespace-nowrap"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {simulatedReseller && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <Eye className="h-4 w-4 text-amber-600 shrink-0" />
            <div className="text-sm text-amber-800">
              <span className="font-semibold">Simulating reseller: {simulatedReseller.name}</span>
              <span className="text-amber-600 ml-2">
                {simulatedReseller.discount_percent}% discount + {simulatedReseller.margin_percent}% margin
              </span>
            </div>
          </div>
        )}

        {activeTab === 'resellers' && <ResellersTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'quotations' && <QuotationsTab />}
        {activeTab === 'smtp' && <SmtpTab />}
        {activeTab === 'api-keys' && <ApiKeysTab />}
      </div>
    </div>
  );
}
